import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import axios from 'axios';
import { getAuthHeaders } from '@/utils/getHeaders';
import createQueryString from '@/utils/queryString';
import { cashbackSettingsAdapter } from '@/adapters/cashbackAdapter';
import type { Cashback, CashbackHistoryItems, NullCashbackHistoryFilters, CashbackInfo, CashbackSettingsAdapted, CashbackHistoryItem } from '@/types/cashback';
import { usePlayerStore } from '@/stores';
import { defaultCurrency } from '@/utils/variables';

export const useCashbackStore = defineStore('cashback', () => {
    const playerStore = usePlayerStore();
    const isPlayer = computed(() => playerStore.isPlayer);
    const player = computed(() => playerStore.player);
    const cashback = ref<Cashback | null>(null);
    const cashbackHistory = ref<CashbackHistoryItems | null>(null);
    const cashbackSettings = ref<CashbackSettingsAdapted | null>(null);
    const loadingFlag = ref<boolean>(false);
    const loadingHistoryFlag = ref<boolean>(false);
    const loadingSettingsFlag = ref<boolean>(false);
    const settingsInitialized = ref<boolean>(false);
    const getCashbackModal = ref<{ flag: boolean; cashback: CashbackHistoryItem | null }>({
        flag: false,
        cashback: null
    });
    function toUpdateCashbackHistoryTransaction(transaction: CashbackHistoryItem) {
        const cashbackTransaction = cashbackHistory.value?.items.find((t) => t.id === transaction.id);
        if (cashbackTransaction) {
            cashbackTransaction.status = transaction.status;
        }
    }
    function toSetGetCashbackModal(flag: boolean, cashback: CashbackHistoryItem | null) {
        getCashbackModal.value = { flag, cashback };
    }
    const awaitToGetCashback = async (): Promise<void> => {
        loadingFlag.value = true;
        try {
            const response = await axios.get<Cashback>('/main-api/cashback/info', getAuthHeaders());
            cashback.value = response.data;
        } catch (error) {
            console.error('Error fetching cashback info:', error);
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    };

    const awaitToGetCashbackHistory = async (filters: NullCashbackHistoryFilters): Promise<void> => {
        loadingHistoryFlag.value = true;
        try {
            const queryString = createQueryString(filters);
            const response = await axios.get<CashbackHistoryItems>(`/main-api/cashback/history${queryString}`, getAuthHeaders());
            cashbackHistory.value = response.data;
        } catch (error) {
            console.error('Error fetching cashback history:', error);
            throw error;
        } finally {
            loadingHistoryFlag.value = false;
        }
    };

    const toResetCashback = (): void => {
        cashback.value = null;
    };

    const awaitToGetCashbackSettings = async (): Promise<void> => {
        if (settingsInitialized.value || loadingSettingsFlag.value) {
            return;
        }

        const currency: string = isPlayer.value ? player.value.currency : defaultCurrency;
        loadingSettingsFlag.value = true;
        try {
            const response = await axios.get<CashbackInfo>('/main-api/platform-config/cashback_settings');
            cashbackSettings.value = cashbackSettingsAdapter(response.data, currency);
            settingsInitialized.value = true;
        } catch (error) {
            console.error('Error fetching cashback settings:', error);
            throw error;
        } finally {
            loadingSettingsFlag.value = false;
        }
    };

    const ensureCashbackSettingsLoaded = async (): Promise<void> => {
        if (!settingsInitialized.value && !loadingSettingsFlag.value) {
            await awaitToGetCashbackSettings();
        }
    };

    const toResetCashbackHistory = (): void => {
        cashbackHistory.value = null;
    };

    const toResetCashbackSettings = (): void => {
        cashbackSettings.value = null;
        settingsInitialized.value = false;
    };

    return {
        // State
        cashback,
        cashbackHistory,
        cashbackSettings,
        loadingFlag,
        loadingHistoryFlag,
        loadingSettingsFlag,
        settingsInitialized,
        getCashbackModal,
        toUpdateCashbackHistoryTransaction,
        toSetGetCashbackModal,
        // Actions
        awaitToGetCashback,
        awaitToGetCashbackHistory,
        awaitToGetCashbackSettings,
        ensureCashbackSettingsLoaded,
        toResetCashback,
        toResetCashbackHistory,
        toResetCashbackSettings
    };
});
